import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import { presetApi } from '../api';
import { presetKeys } from './preset.keys';

type UploadGalleryItemInput = {
  beforeFile: File;
  afterFile: File;
  data: {
    presetId: string;
    before: string;
    after: string;
    createdById: string;
    displayOrder: number;
  };
};

export function useUploadPresetGallery(
  presetId?: string,
  options?: UseMutationOptions<void, Error, UploadGalleryItemInput>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['presets', 'upload-gallery'],
    mutationFn: ({ beforeFile, afterFile, data }: UploadGalleryItemInput) =>
      presetApi.uploadGalleryItem(beforeFile, afterFile, data),
    ...mutationOptions,
    onSuccess: (_, variables, onMutateResult, context) => {
      if (presetId) {
        queryClient.invalidateQueries({ queryKey: presetKeys.galleries(presetId) });
      }
      queryClient.invalidateQueries({ queryKey: presetKeys.all });
      providedOnSuccess?.(_, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao fazer upload da galeria',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  const uploadGalleryItem = (input: UploadGalleryItemInput) => mutation.mutateAsync(input);
  return {
    uploadGalleryItem,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
